import type {
  EmptyObject,
  JsonArray,
  JsonObject,
  JsonPrimitive,
  JsonValue,
} from 'type-fest'
import { z } from 'zod'

import type {
  NonEmptyArray,
  PositiveInteger,
  ZodSimplify,
} from '@mntn-dev/utility-types'

import { EnumBuilder } from '@mntn-dev/utility-types'
import { coerceDate } from '../preprocessors.ts'
import { validatePhoneNumber } from '../utils/phone-number/validation.ts'

/**
 * EmptyObject
 */
export const EmptyObjectSchema = z.object<EmptyObject>({}).strict()

/**
 * NonEmptyString
 */
export const NonEmptyStringSchema = <Max extends number, Min extends number>(
  max?: PositiveInteger<Max>,
  min?: PositiveInteger<Min>,
  messages?: {
    max?: string
    min?: string
  }
) => {
  const nonEmptyString = z
    .string()
    .trim()
    .min(min ?? 1, messages?.min)
  return max ? nonEmptyString.max(max, messages?.max) : nonEmptyString
}

/**
 * Optional NonEmptyString
 */
export const OptionalNonEmptyStringSchema = <
  Max extends number,
  Min extends number,
>(
  max?: PositiveInteger<Max>,
  min?: PositiveInteger<Min>,
  messages?: {
    max?: string
    min?: string
  }
) => {
  let schema = z.string().trim()

  // only include a min if its greater than 1,
  // otherwise an unintended min error will happen on empty string
  if (min && min > 1) {
    schema = schema.min(min, messages?.min)
  }

  if (max) {
    schema = schema.max(max, messages?.max)
  }

  return schema.transform((val) => (val === '' ? undefined : val)).optional()
}

/**
 * AgreementCheck
 */
export const AgreementCheckSchema = z.object({
  agreementCheck: z.boolean(),
})

export type AgreementCheck = ZodSimplify<typeof AgreementCheckSchema>

/**
 * PositiveInteger
 */
export const PositiveIntegerSchema = z.number().int().positive()

/**
 * PhoneNumber
 */
export const PhoneNumberSchema = z.string().min(7).max(20)
export type PhoneNumber = ZodSimplify<typeof PhoneNumberSchema>

/**
 * CompanyName
 */
export const CompanyNameSchema = NonEmptyStringSchema(256)
export type CompanyName = ZodSimplify<typeof CompanyNameSchema>

/**
 * Decimal
 *
 * A number that is rounded to a specific precision
 */
export const DecimalSchema = <Precision extends number>(
  decimals?: PositiveInteger<Precision>,
  schema: z.ZodNumber = z.number()
) => {
  return schema.transform((value) => {
    const precision = 10 ** (decimals ?? 0)
    return Math.floor(value * precision) / precision
  })
}

export type Decimal = ZodSimplify<ReturnType<typeof DecimalSchema>>

/**
 * DollarsSchema: a 2-decimal floating point value representing USD
 */
export const DollarsSchema = <Max extends number>(
  max?: PositiveInteger<Max>
) => {
  const schema = z.number().nonnegative()
  return (max !== undefined ? schema.max(max) : schema).transform(
    (val) => Math.round(val * 100) / 100
  )
}

export type Dollars = ZodSimplify<ReturnType<typeof DollarsSchema>>

/**
 * CountSchema
 */
export const CountSchema = z.number().int().nonnegative()
export type Count = ZodSimplify<typeof CountSchema>

/**
 * CostMarginPercent
 */
export const CostMarginPercentSchema = z.number().int().nonnegative().max(99)
export type CostMarginPercent = ZodSimplify<typeof CostMarginPercentSchema>

/**
 * DataOrigin - This is used to track the origin of a data
 * that was imported into this application from an external
 * source.
 */
export const [DataOrigins, DataOriginSchema, DataOriginEnum] =
  EnumBuilder('qontrol')

export type DataOrigin = ZodSimplify<typeof DataOriginSchema>

/**
 * Date (or ISO8601 string)
 */

export const DateSchema = z.union([
  z.date(),
  z
    .union([z.string().date(), z.string().datetime()])
    .transform((value) => new Date(value)),
])

/**
 * Description
 */
export const MaxDescriptionLength = 4096
export const DescriptionSchema = NonEmptyStringSchema(MaxDescriptionLength)
export type Description = ZodSimplify<typeof DescriptionSchema>

/**
 * EmailAddress
 */
export const EmailAddressSchema = z.string().email()
export type EmailAddress = ZodSimplify<typeof EmailAddressSchema>

/**
 * LanguageId
 */
export const [LanguageIds, LanguageIdSchema, LanguageIdEnum] = EnumBuilder(
  'en',
  'es'
).default('en')
export type LanguageId = ZodSimplify<typeof LanguageIdSchema>

/**
 * Name
 */
export const MaxNameLength = 256
export const NameSchema = NonEmptyStringSchema(MaxNameLength)
export type Name = ZodSimplify<typeof NameSchema>

/**
 * Note
 */
export const MaxNoteLength = 5000
export const MeaningfulNoteLength = 100

export const NoteSchema = NonEmptyStringSchema(MaxNoteLength)
export type Note = ZodSimplify<typeof NoteSchema>

export const OptionalNoteSchema = z.string().max(MaxNoteLength).optional()
export type OptionalNote = ZodSimplify<typeof OptionalNoteSchema>

export const MeaningfulNoteSchema = NonEmptyStringSchema(
  MaxNoteLength,
  MeaningfulNoteLength
)
export type MeaningfulNote = ZodSimplify<typeof MeaningfulNoteSchema>

/**
 * Phone Number
 */
const e164rx = /^\+\d+$/

const E164PhoneNumberSchema = z.string().refine((value) => {
  return typeof value === 'string' ? e164rx.test(value) : false
})

export const USPhoneNumberSchema = E164PhoneNumberSchema.superRefine(
  (value, ctx) => {
    if (value.length > 12) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: 12,
        type: 'string',
        inclusive: true,
        message: 'US phone numbers must be 12 characters long (+1XXXXXXXXXX)',
      })
    }

    if (value.length < 12) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 12,
        type: 'string',
        inclusive: true,
        message: 'US phone numbers must be 12 characters long (+1XXXXXXXXXX)',
      })
    }

    const results = validatePhoneNumber(value)

    if (results === 'invalid-country-code') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Only US phone numbers are currently supported',
      })
    }

    if (results !== 'valid') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid US phone number',
      })
    }
  }
)

export type USPhoneNumberSchema = ZodSimplify<typeof USPhoneNumberSchema>

/**
 * ProposalVersion
 */
export const ProposalVersionSchema = z.number().int().nonnegative()
export type ProposalVersion = ZodSimplify<typeof ProposalVersionSchema>

/**
 * Timestamp
 */
export const TimestampSchema = z.preprocess(coerceDate, z.date())
export type Timestamp = ZodSimplify<typeof TimestampSchema>

/**
 * Translations
 */
export const TranslationsSchema = <Properties extends NonEmptyArray<string>>(
  ...properties: Properties
) =>
  z.record(
    LanguageIdSchema,
    z.record(z.enum(properties), NonEmptyStringSchema())
  )

/**
 * UnixTimestamp
 */
export const UnixTimestampSchema = z
  .number()
  .int()
  .positive()
  .refine(
    (val) => {
      return val >= 0 && val <= 2147483647 // Optional range check (32-bit max timestamp)
    },
    {
      message: 'Invalid Unix timestamp',
    }
  )

export type UnixTimestamp = ZodSimplify<typeof UnixTimestampSchema>

/**
 * Url
 */
export const UrlSchema = z.string().url()
export type Url = ZodSimplify<typeof UrlSchema>

/**
 * JsonValue
 * Inspired by https://zod.dev/?id=json-type
 */
export const JsonPrimitiveSchema: z.ZodType<JsonPrimitive> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
])

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    // JsonPrimitive
    JsonPrimitiveSchema,
    // JsonObject
    z.record(JsonValueSchema),
    // JsonArray
    z.array(JsonValueSchema),
  ])
)

export const JsonObjectSchema: z.ZodType<JsonObject> = z.record(JsonValueSchema)
export const JsonArraySchema: z.ZodType<JsonArray> = z.array(JsonValueSchema)

/**
 * Byte sizes
 */
export const KB = 1024
export const MB = 1024 * KB
export const GB = 1024 * MB
